import React, { useState, useEffect, useCallback } from 'react';
import { Users, Trophy, ChevronRight, Check, Clock, Plus, Swords, Target, Send, Flame, Heart, Sparkles, Star, MapPin, MessageCircle, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const CATEGORY_COLORS = {
  social: 'bg-blue-100 text-blue-700',
  kindness: 'bg-pink-100 text-pink-700',
  listening: 'bg-green-100 text-green-700',
  confidence: 'bg-amber-100 text-amber-700',
  assertiveness: 'bg-purple-100 text-purple-700',
  gratitude: 'bg-teal-100 text-teal-700',
  empathy: 'bg-indigo-100 text-indigo-700',
};

const REACTION_EMOJIS = {
  fire: { icon: Flame, color: 'text-orange-500', bg: 'bg-orange-50' },
  clap: { icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  heart: { icon: Heart, color: 'text-pink-500', bg: 'bg-pink-50' },
  star: { icon: Star, color: 'text-amber-500', bg: 'bg-amber-50' },
};

const FriendChallenges = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [friendEmail, setFriendEmail] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [streak, setStreak] = useState(null);
  const [coachTip, setCoachTip] = useState(null);
  const [replay, setReplay] = useState(null);
  const [groupMode, setGroupMode] = useState(false);
  const [groupEmails, setGroupEmails] = useState(['', '']);
  const [betAmount, setBetAmount] = useState(50);
  const [bettingId, setBettingId] = useState(null);
  // Sprint 2026-07-24 — action feedback toast. Replaces the earlier
  // empty-catch pattern that silently swallowed every accept/decline/
  // react/log/bet error, so users see a real message on both success
  // and failure. Auto-dismisses after 3.5s; sits below the Coach Tip.
  const [actionToast, setActionToast] = useState(null);
  const showActionToast = useCallback((type, message) => {
    setActionToast({ type, message });
    setTimeout(() => setActionToast(null), 3500);
  }, []);
  const humanErrorMessage = (err, fallback) => {
    const raw = err?.message || '';
    // Strip common "HTTP 404: " / "HTTP 401: " prefixes from apiClient
    const m = raw.match(/^HTTP\s+(\d{3}):?\s*(.*)$/i);
    if (m) {
      const code = m[1];
      const detail = (m[2] || '').replace(/^{.*"detail":\s*"([^"]+)".*}$/, '$1');
      if (code === '404') return detail || fallback;
      if (code === '401') return 'You need to be signed in to do that.';
      if (code === '403') return 'You do not have permission for this action.';
      return detail || fallback;
    }
    return raw || fallback;
  };

  const fetchChallenges = useCallback(async () => {
    try {
      const [cData, tData, sData] = await Promise.all([
        apiClient.request('/api/advanced-engagement/friend-challenges'),
        apiClient.request('/api/advanced-engagement/friend-challenges/templates'),
        apiClient.request('/api/advanced-engagement/challenge-streak').catch(() => null),
      ]);
      setChallenges(cData.challenges || []);
      setTemplates(tData.templates || []);
      if (sData) setStreak(sData);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const handleCreate = async () => {
    if (groupMode) {
      const emails = groupEmails.filter(e => e.trim());
      if (emails.length < 2) { setError('Group challenges need at least 2 friends'); return; }
      setCreating(true);
      setError('');
      try {
        await apiClient.request('/api/advanced-engagement/group-challenges', {
          method: 'POST',
          body: JSON.stringify({ friend_emails: emails, template_index: selectedTemplate }),
        });
        setShowCreate(false);
        setGroupEmails(['', '']);
        setSelectedTemplate(null);
        fetchChallenges();
      } catch (err) { setError(err.message || 'Failed to create group challenge'); }
      setCreating(false);
    } else {
      if (!friendEmail.trim()) { setError('Enter your friend\'s email'); return; }
      setCreating(true);
      setError('');
      try {
        const resp = await apiClient.request('/api/advanced-engagement/friend-challenges', {
          method: 'POST',
          body: JSON.stringify({ friend_email: friendEmail.trim(), template_index: selectedTemplate }),
        });
        // Sprint 2026-07-24 — backend now returns a structured
        // {success: false, reason: 'friend_not_found', message: ...}
        // for non-HiBFF invitees so we can render a clean error inline
        // instead of falling into the catch block on a 404.
        if (resp && resp.success === false) {
          setError(resp.message || 'That friend is not on HiBFF yet.');
        } else {
          setShowCreate(false);
          setFriendEmail('');
          setSelectedTemplate(null);
          fetchChallenges();
          showActionToast('success', 'Challenge invite sent — an email is on its way to your friend.');
        }
      } catch (err) { setError(humanErrorMessage(err, 'Failed to create challenge')); }
      setCreating(false);
    }
  };

  const handleAction = async (challengeId, action) => {
    try {
      await apiClient.request(`/api/advanced-engagement/friend-challenges/${challengeId}/${action}`, { method: 'POST' });
      fetchChallenges();
      const successMsg = {
        accept:   'Challenge accepted — good luck!',
        decline:  'Challenge declined.',
        complete: 'Nice work — challenge marked complete!',
      }[action] || 'Done.';
      showActionToast('success', successMsg);
    } catch (err) {
      const fallback = {
        accept:   'Challenge not found or already accepted.',
        decline:  'Could not decline — challenge may already be resolved.',
        complete: 'Could not mark complete — please try again.',
      }[action] || 'Something went wrong.';
      showActionToast('error', humanErrorMessage(err, fallback));
    }
  };

  const handleLogProgress = async (challengeId, isGroup = false) => {
    try {
      const endpoint = isGroup
        ? `/api/advanced-engagement/group-challenges/${challengeId}/log`
        : `/api/advanced-engagement/friend-challenges/${challengeId}/log`;
      await apiClient.request(endpoint, { method: 'POST', body: JSON.stringify({ note: '' }) });
      // Get coach tip
      const tip = await apiClient.request(`/api/advanced-engagement/friend-challenges/${challengeId}/coach-tip`, { method: 'POST' });
      setCoachTip(tip);
      setTimeout(() => setCoachTip(null), 8000);
      fetchChallenges();
      // Check streak
      apiClient.request('/api/advanced-engagement/challenge-streak/check', { method: 'POST' }).catch(() => {});
      showActionToast('success', 'Progress logged!');
    } catch (err) {
      showActionToast('error', humanErrorMessage(err, 'Could not log progress — please try again.'));
    }
  };

  const handleReact = async (challengeId, emoji) => {
    try {
      await apiClient.request(`/api/advanced-engagement/friend-challenges/${challengeId}/react`, {
        method: 'POST',
        body: JSON.stringify({ emoji }),
      });
      fetchChallenges();
    } catch (err) {
      showActionToast('error', humanErrorMessage(err, 'Could not send reaction.'));
    }
  };

  const handleViewReplay = async (challengeId) => {
    try {
      const data = await apiClient.request(`/api/advanced-engagement/friend-challenges/${challengeId}/replay`);
      setReplay(data);
    } catch (err) {
      showActionToast('error', humanErrorMessage(err, 'Replay is not available yet for this challenge.'));
    }
  };

  const handlePlaceBet = async () => {
    if (!bettingId || betAmount < 10) return;
    try {
      await apiClient.request(`/api/community/challenges/${bettingId}/bet`, {
        method: 'POST',
        body: JSON.stringify({ hearts: betAmount }),
      });
      setBettingId(null);
      setBetAmount(50);
      fetchChallenges();
      showActionToast('success', `Bet of ${betAmount} hearts placed!`);
    } catch (err) {
      showActionToast('error', humanErrorMessage(err, 'Could not place bet — please try again.'));
    }
  };

  const activeChallenges = challenges.filter(c => c.status === 'active');
  const pendingChallenges = challenges.filter(c => c.status === 'pending');
  const pastChallenges = challenges.filter(c => ['completed', 'declined', 'expired'].includes(c.status));

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-4xl mx-auto" data-testid="friend-challenges-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Swords size={28} className="text-indigo-600" />
              Friend Challenges
            </h1>
            <p className="text-sm text-gray-500 mt-1">Practice together, grow together</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/meetups')}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all"
              data-testid="view-meetups-btn"
            >
              <MapPin size={16} />
              <span className="hidden sm:inline">Meetups</span>
            </button>
            <button
              onClick={() => navigate('/leaderboard')}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all"
              data-testid="view-leaderboard-btn"
            >
              <Trophy size={16} />
              <span className="hidden sm:inline">Leaderboard</span>
            </button>
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all"
              data-testid="create-challenge-btn"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">New</span>
            </button>
          </div>
        </div>

        {/* Challenge Streak Banner */}
        {streak && streak.weekly_streak > 0 && (
          <div className={`mb-4 p-3 rounded-xl flex items-center justify-between ${streak.nft_earned ? 'bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-300' : 'bg-indigo-50 border border-indigo-200'}`} data-testid="challenge-streak-banner">
            <div className="flex items-center gap-2">
              <Flame size={20} className={streak.nft_earned ? 'text-amber-500' : 'text-indigo-500'} />
              <div>
                <p className="text-sm font-bold text-gray-900">{streak.weekly_streak}-week streak</p>
                <p className="text-[10px] text-gray-500">
                  {streak.nft_earned ? 'Social Champion NFT earned!' : `${4 - streak.weekly_streak} more for Social Champion NFT`}
                </p>
              </div>
            </div>
            {streak.nft_earned && <Sparkles size={18} className="text-amber-500" />}
          </div>
        )}

        {/* Coach Tip Toast */}
        {coachTip && (
          <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl animate-in slide-in-from-top-2" data-testid="coach-tip-toast">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-violet-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle size={14} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-bold text-violet-700 uppercase tracking-wider mb-1">AvA Coach Tip</p>
                <p className="text-sm text-gray-800">{coachTip.coach_tip}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action feedback toast (Sprint 2026-07-24) */}
        {actionToast && (
          <div
            className={`mb-4 p-3 rounded-xl border animate-in slide-in-from-top-2 ${
              actionToast.type === 'success'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
            data-testid={`action-toast-${actionToast.type}`}
          >
            <p className={`text-sm font-medium ${actionToast.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {actionToast.message}
            </p>
          </div>
        )}

        {/* Replay Modal */}
        {replay && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="challenge-replay-modal">
              <div className="text-center mb-4">
                <Sparkles size={28} className="text-amber-500 mx-auto mb-2" />
                <h3 className="text-lg font-bold text-gray-900">Challenge Replay</h3>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${CATEGORY_COLORS[replay.category] || 'bg-gray-100'}`}>{replay.title}</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">{replay.summary}</p>
              <p className="text-xs text-gray-400 text-center mb-4">{replay.participants}</p>
              <button onClick={() => setReplay(null)} className="w-full py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl">Close</button>
            </div>
          </div>
        )}

        {/* Create Challenge Panel */}
        {showCreate && (
          <div className="mb-6 p-5 bg-white rounded-2xl border-2 border-indigo-200 shadow-sm" data-testid="create-challenge-panel">
            {/* 1v1 vs Group Toggle */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setGroupMode(false)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${!groupMode ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}
                data-testid="mode-1v1"
              >
                1v1 Challenge
              </button>
              <button
                onClick={() => setGroupMode(true)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${groupMode ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}
                data-testid="mode-group"
              >
                Group Challenge
              </button>
            </div>

            {/* Friend Email(s) */}
            {groupMode ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Friends&apos; Emails (2-4)</label>
                {groupEmails.map((email, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <div className="relative flex-1">
                      <Users size={14} className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { const ne = [...groupEmails]; ne[i] = e.target.value; setGroupEmails(ne); }}
                        placeholder={`Friend ${i + 1}`}
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        data-testid={`group-email-${i}`}
                      />
                    </div>
                    {i >= 2 && (
                      <button onClick={() => setGroupEmails(groupEmails.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500 text-sm">Remove</button>
                    )}
                  </div>
                ))}
                {groupEmails.length < 4 && (
                  <button onClick={() => setGroupEmails([...groupEmails, ''])} className="text-xs text-indigo-600 font-medium hover:text-indigo-700">+ Add another friend</button>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Friend&apos;s Email</label>
                <div className="relative">
                  <Send size={16} className="absolute left-3 top-2.5 text-gray-400" />
                  <input type="email" value={friendEmail} onChange={(e) => setFriendEmail(e.target.value)} placeholder="friend@email.com" className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm" data-testid="challenge-friend-email" />
                </div>
              </div>
            )}

            {/* Template Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Pick a Challenge</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {templates.map(t => (
                  <button
                    key={t.index}
                    onClick={() => setSelectedTemplate(t.index)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      selectedTemplate === t.index
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`challenge-template-${t.index}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CATEGORY_COLORS[t.category] || 'bg-gray-100 text-gray-700'}`}>
                        {t.category}
                      </span>
                      <span className="text-xs text-gray-400">{t.duration_days}d</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{t.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-red-600 mb-3" data-testid="challenge-error">{error}</p>}

            <div className="flex gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg text-sm hover:shadow-md disabled:opacity-50"
                data-testid="send-challenge-btn"
              >
                {creating ? 'Sending...' : 'Send Challenge'}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading challenges...</div>
        ) : (
          <>
            {/* Pending Invites */}
            {pendingChallenges.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Pending Invites</h2>
                <div className="space-y-3">
                  {pendingChallenges.map(c => (
                    <div key={c.challenge_id} className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid={`pending-challenge-${c.challenge_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">{c.title}</p>
                          <p className="text-xs text-gray-500">From {c.creator_name}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CATEGORY_COLORS[c.category] || 'bg-gray-100'}`}>
                          {c.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{c.description}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAction(c.challenge_id, 'decline')}
                          className="flex-1 py-2 border border-gray-300 text-gray-600 font-medium rounded-lg text-sm hover:bg-gray-50"
                          data-testid={`decline-challenge-${c.challenge_id}`}
                        >
                          Decline
                        </button>
                        <button
                          onClick={() => handleAction(c.challenge_id, 'accept')}
                          className="flex-1 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg text-sm hover:shadow-md"
                          data-testid={`accept-challenge-${c.challenge_id}`}
                        >
                          Accept
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Challenges */}
            {activeChallenges.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Active Challenges</h2>
                <div className="space-y-3">
                  {activeChallenges.map(c => {
                    const myProgress = c.creator_progress; // We simplify by showing creator for now
                    const friendProgress = c.friend_progress;
                    const myPercent = Math.min(100, (myProgress / c.goal_count) * 100);
                    const friendPercent = Math.min(100, (friendProgress / c.goal_count) * 100);

                    return (
                      <div key={c.challenge_id} className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm" data-testid={`active-challenge-${c.challenge_id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-bold text-gray-900">{c.title}</p>
                            <p className="text-xs text-gray-500">with {c.friend_name}</p>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-400">
                            <Clock size={12} />
                            <span>{c.duration_days}d</span>
                          </div>
                        </div>

                        {/* Progress Bars */}
                        <div className="space-y-2 mb-3">
                          <div>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-medium text-gray-700">You</span>
                              <span className="text-gray-500">{myProgress}/{c.goal_count}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all" style={{ width: `${myPercent}%` }} />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-medium text-gray-700">{c.friend_name?.split(' ')[0]}</span>
                              <span className="text-gray-500">{friendProgress}/{c.goal_count}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-pink-500 to-rose-500 rounded-full transition-all" style={{ width: `${friendPercent}%` }} />
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleLogProgress(c.challenge_id, c.type === 'group')}
                          className="w-full py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-semibold rounded-lg hover:shadow-md transition-all flex items-center justify-center gap-1.5"
                          data-testid={`log-progress-${c.challenge_id}`}
                        >
                          <Target size={14} />
                          Log Progress
                        </button>

                        {/* Reactions */}
                        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-100">
                          <span className="text-[10px] text-gray-400 mr-1">React:</span>
                          {Object.entries(REACTION_EMOJIS).map(([key, { icon: Icon, color, bg }]) => {
                            const count = (c.reactions || []).filter(r => r.emoji === key).length;
                            return (
                              <button
                                key={key}
                                onClick={() => handleReact(c.challenge_id, key)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${count > 0 ? bg + ' ' + color : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                                data-testid={`react-${key}-${c.challenge_id}`}
                              >
                                <Icon size={12} />
                                {count > 0 && <span className="font-bold">{count}</span>}
                              </button>
                            );
                          })}
                          {!c.bets && c.type !== 'group' && (
                            <button onClick={() => setBettingId(c.challenge_id)} className="ml-auto flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-semibold hover:bg-amber-100" data-testid={`bet-${c.challenge_id}`}>
                              <Heart size={10} /> Bet
                            </button>
                          )}
                          {c.bets && (
                            <span className="ml-auto text-[10px] text-amber-500 font-bold flex items-center gap-0.5"><Heart size={10} /> Bet placed</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past Challenges */}
            {pastChallenges.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Past Challenges</h2>
                <div className="space-y-2">
                  {pastChallenges.slice(0, 10).map(c => (
                    <div key={c.challenge_id} className="p-3 bg-gray-50 rounded-xl flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{c.title}</p>
                        <p className="text-xs text-gray-400">
                          {c.type === 'group' ? `Group (${c.members?.length || 0})` : `with ${c.friend_name}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {c.status === 'completed' && (
                          <button
                            onClick={() => handleViewReplay(c.challenge_id)}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                            data-testid={`replay-${c.challenge_id}`}
                          >
                            Replay
                          </button>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          c.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {c.status === 'completed' ? 'Completed' : c.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {challenges.length === 0 && !showCreate && (
              <div className="text-center py-16" data-testid="challenges-empty">
                <div className="w-16 h-16 mx-auto mb-4 bg-indigo-100 rounded-2xl flex items-center justify-center">
                  <Users size={32} className="text-indigo-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No challenges yet</h3>
                <p className="text-sm text-gray-500 mb-4 max-w-xs mx-auto">
                  Challenge a friend to practice social skills together. It&apos;s more fun with a buddy!
                </p>
                <button
                  onClick={() => setShowCreate(true)}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                >
                  Start Your First Challenge
                </button>
              </div>
            )}

            {/* Betting Modal */}
            {bettingId && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-xs" data-testid="betting-modal">
                  <h3 className="font-bold text-gray-900 mb-1 text-center">Wager Hearts</h3>
                  <p className="text-xs text-gray-500 text-center mb-4">First to finish wins the pot!</p>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    {[10, 25, 50, 100].map(amt => (
                      <button key={amt} onClick={() => setBetAmount(amt)} className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${betAmount === amt ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}>
                        {amt}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setBettingId(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
                    <button onClick={handlePlaceBet} className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg text-sm" data-testid="confirm-bet-btn">Bet {betAmount}</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FriendChallenges;
